"use client";

import { useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
      Autoplay,
      Pagination,
      EffectFade,
      Keyboard,
      A11y,
} from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

import { HeroSection as HeroSectionType, HeroSlide } from "@/types/home";
import { getStrapiImageUrl } from "@/lib/strapiImage";

const AUTO_DELAY = 7000;
const INTERACTED_DELAY = 15000;

interface HeroSectionProps {
      data: HeroSectionType;
}

export default function HeroSection({ data }: HeroSectionProps) {
      const swiperRef = useRef<SwiperType | null>(null);
      const [interacted, setInteracted] = useState(false);

      const slides = useMemo<HeroSlide[]>(() => {
            if (data.slides && data.slides.length > 0) return data.slides;

            return [
                  {
                        id: data.id,
                        title: data.title,
                        description: data.description,
                        buttonText: data.buttonText,
                        buttonUrl: data.buttonUrl,
                        heroMedia: data.heroMedia,
                        heroMediaAlt: data.heroMediaAlt,
                  },
            ];
      }, [data]);

      const slowDownAutoplay = () => {
            const swiper = swiperRef.current;

            if (!swiper || swiper.destroyed) return;

            if (!interacted) setInteracted(true);

            if (
                  swiper.params.autoplay &&
                  typeof swiper.params.autoplay === "object"
            ) {
                  swiper.params.autoplay.delay = INTERACTED_DELAY;
            }

            swiper.autoplay?.stop();
            swiper.autoplay?.start();
      };

      const isSingle = slides.length <= 1;

      return (
            <section className="relative w-full min-h-[70vh] h-[70vh] overflow-hidden group">
                  <Swiper
                        modules={[Autoplay, Pagination, EffectFade, Keyboard, A11y]}
                        effect="fade"
                        fadeEffect={{ crossFade: true }}
                        speed={900}
                        loop={!isSingle}
                        keyboard={{ enabled: true }}
                        a11y={{ enabled: true }}
                        autoplay={
                              isSingle
                                    ? false
                                    : {
                                          delay: AUTO_DELAY,
                                          disableOnInteraction: false,
                                          pauseOnMouseEnter: false,
                                    }
                        }
                        pagination={{
                              clickable: true,
                              bulletClass: "hero-bullet",
                              bulletActiveClass: "hero-bullet-active",
                        }}
                        onSwiper={(s) => {
                              swiperRef.current = s;
                        }}
                        onSliderFirstMove={slowDownAutoplay}
                        onTouchEnd={slowDownAutoplay}
                        className="w-full h-full"
                  >
                        {slides.map((slide, index) => {
                              const imageUrl =
                                    getStrapiImageUrl(slide.heroMedia, "original") ||
                                    "/placeholder.jpg";

                              return (
                                    <SwiperSlide key={slide.id}>
                                          <div className="relative w-full h-full">
                                                <div className="absolute inset-0 z-0">
                                                      <Image
                                                            src={imageUrl}
                                                            alt={
                                                                  slide.heroMediaAlt ||
                                                                  slide.heroMedia?.alternativeText ||
                                                                  slide.title ||
                                                                  ""
                                                            }
                                                            fill
                                                            priority={index === 0}
                                                            quality={90}
                                                            unoptimized
                                                            className="object-cover object-center"
                                                            sizes="100vw"
                                                      />

                                                      <div className="absolute inset-0 bg-linear-to-r from-black/80 via-black/40 to-transparent" />
                                                </div>

                                                <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                                                      <div className="max-w-2xl space-y-5">
                                                            {slide.title && (
                                                                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.1] drop-shadow-lg text-left">
                                                                        {slide.title}
                                                                  </h1>
                                                            )}

                                                            {slide.description && (
                                                                  <p className="text-base sm:text-lg text-gray-100/90 leading-relaxed drop-shadow-md text-left max-w-xl">
                                                                        {slide.description}
                                                                  </p>
                                                            )}

                                                            {slide.buttonText && slide.buttonUrl && (
                                                                  <div className="pt-2">
                                                                        <Link
                                                                              href={slide.buttonUrl as any}
                                                                              className="inline-flex items-center justify-center px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 bg-[#A68F72] rounded-xl hover:bg-[#947d61] hover:-translate-y-0.5 shadow-lg shadow-[#A68F72]/20"
                                                                        >
                                                                              {slide.buttonText}
                                                                        </Link>
                                                                  </div>
                                                            )}
                                                      </div>
                                                </div>
                                          </div>
                                    </SwiperSlide>
                              );
                        })}
                  </Swiper>

                  {!isSingle && (
                        <>
                              <button
                                    type="button"
                                    aria-label="Previous slide"
                                    onClick={() => {
                                          swiperRef.current?.slidePrev();
                                          slowDownAutoplay();
                                    }}
                                    className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-white/25 transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 focus:opacity-100"
                              >
                                    <ArrowLeft size={20} />
                              </button>

                              <button
                                    type="button"
                                    aria-label="Next slide"
                                    onClick={() => {
                                          swiperRef.current?.slideNext();
                                          slowDownAutoplay();
                                    }}
                                    className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white hover:bg-white/25 transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 focus:opacity-100"
                              >
                                    <ArrowRight size={20} />
                              </button>
                        </>
                  )}

                  <style jsx global>{`
                .swiper-pagination {
                    position: absolute;
                    bottom: 1.5rem !important;
                    left: 0;
                    right: 0;
                    z-index: 20;
                    display: flex;
                    gap: 0.5rem;
                    justify-content: center;
                }

                .hero-bullet {
                    width: 28px;
                    height: 4px;
                    border-radius: 9999px;
                    background: rgba(255, 255, 255, 0.4);
                    cursor: pointer;
                    transition: background 0.3s ease, width 0.3s ease;
                    display: inline-block;
                }

                .hero-bullet-active {
                    background: #ffffff;
                    width: 44px;
                }
            `}</style>
            </section>
      );
}
